import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import { ActivityTableSkeleton } from "../components/ui/PageContentSkeleton";
import ActivityLogListItem from "../components/activity/ActivityLogListItem";

export default function TeamActivity() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  useEffect(() => {
    api.get("/manager/dashboard").then((res) => {
      const data = res.data?.data || res.data;
      setEmployees(data?.managedEmployees || []);
    }).catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [employeeId, entityFilter, actionFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (employeeId) params.employeeId = employeeId;
      if (entityFilter) params.entityType = entityFilter;
      if (actionFilter) params.action = actionFilter;
      const res = await api.get("/activity-logs/team", { params });
      setLogs(Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []));
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load team activity");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary mb-1">Team Activity</h1>
          <p className="text-gray-600">
            Activity for your team: actions on their accounts and actions you take as manager/admin on their
            behalf (who did what, for whom). Filter by employee or action.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-full sm:w-auto"
          >
            <option value="">All employees</option>
            {employees.map((emp) => (
              <option key={emp.id || emp._id} value={emp.id || emp._id}>{emp.name || emp.email}</option>
            ))}
          </select>
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-full sm:w-auto"
          >
            <option value="">All types</option>
            <option value="goal">Goals</option>
            <option value="employee">Employees</option>
            <option value="leave">Leave</option>
            <option value="expense">Expenses</option>
            <option value="attendance">Attendance</option>
          </select>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-full sm:w-auto"
          >
            <option value="">All actions</option>
            <option value="goal_created">Goal created</option>
            <option value="leave_requested">Leave requested</option>
            <option value="leave_approved">Leave approved</option>
            <option value="expense_requested">Expense requested</option>
            <option value="attendance_checked_in">Check in</option>
            <option value="attendance_checked_out">Check out</option>
            <option value="holiday_created">Holiday created</option>
            <option value="holiday_updated">Holiday updated</option>
            <option value="holiday_deleted">Holiday deleted</option>
          </select>
        </div>
      </div>

      {loading ? (
        <ActivityTableSkeleton />
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">No team activity logs found.</div>
      ) : (
        <div className="bg-white rounded-xl shadow p-6">
          <ul className="space-y-2">
            {logs.map((log) => (
              <ActivityLogListItem key={log.id} entry={log} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
